import {FetchPolicy, gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, PublishingStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Link from 'ui-library/Link';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {
  ProceduresDropdownListingQuery_proceduresListing_ProcedureNode as ProcedureDropdownTemplate,
  ProceduresDropdownListingQuery,
} from './__generated/ProceduresDropdownListingQuery';
import useProcedureValidation, {
  ProcedureValidation,
  ProcedureValidationStatus,
} from './useProcedureValidation';

export const PROCEDURES_DROPDOWN_LISTING_QUERY = gql`
  query ProceduresDropdownListingQuery($procedureTypes: [ProcedureType!])
  @skipAudit
  @engComponent(name: STUDIES) {
    proceduresListing(procedureTypes: $procedureTypes, excludeArchived: true) {
      id
      name
      templateCollection {
        id
        name
      }
      currentPublishingRecord {
        ... on PublishingRecordNode {
          id
          publishingStatus
        }
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ProcedureDropdownProps {
  isDisabled: boolean;
  procedureType: ProcedureType;
  selectedProcedureId: string | null;
  onChangeSelectedProcedure: (procedureId: string | null) => void;
  onRequestCloseModal: () => void;
  placeholderText?: string;
  procedureFetchPolicy: FetchPolicy;
}

interface ProcedureDropdownQueryData {
  procedures: Array<ProcedureDropdownTemplate> | null;
}

/**
 * @package exported for testing
 */
export function getProcedureDisplayText(template: ProcedureDropdownTemplate): string {
  return `${template.templateCollection.name} / ${template.name}`;
}

function makeOptionData(procedure: ProcedureDropdownTemplate): OptionData {
  return {
    name: getProcedureDisplayText(procedure),
    value: procedure.id,
    icon: ModelTypeConstants.PROCEDURE,
    className: 'newDropdown-schemaOption',
    extra: {unstarrable: false},
  };
}

type PresentationalProcedureDropdownProps = ProcedureDropdownProps & ProcedureDropdownQueryData;

export function PresentationalProcedureDropdown(props: PresentationalProcedureDropdownProps): JSX.Element {
  const {onChangeSelectedProcedure, selectedProcedureId} = props;
  const optionDatas = props.procedures?.map((procedure) => makeOptionData(procedure));

  const [stagedProcedureIdForValidation, setStagedProcedureIdForValidation] = useState<string | null>(
    selectedProcedureId
  );
  const stagedProcedureForValidation = props.procedures?.find(
    (procedure) => procedure.id === stagedProcedureIdForValidation
  );
  const procedureValidation: ProcedureValidation = useProcedureValidation(
    stagedProcedureIdForValidation,
    props.procedureFetchPolicy
  );

  // Notify the parent component AFTER useProcedureValidation has validated the newly selected procedure
  useEffect(() => {
    if (
      procedureValidation.validationStatus === ProcedureValidationStatus.VALID &&
      selectedProcedureId !== procedureValidation.id
    ) {
      onChangeSelectedProcedure(procedureValidation.id);
    } else if (
      procedureValidation.validationStatus === ProcedureValidationStatus.IN_PROGRESS ||
      procedureValidation.validationStatus === ProcedureValidationStatus.INVALID
    ) {
      onChangeSelectedProcedure(null);
    }
    // We don't care to re-run this if only onChangeSelectedProcedure has changed. It would cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedureValidation.id, procedureValidation.validationStatus, selectedProcedureId]);

  return (
    <>
      <SelectDropdown
        disabled={props.isDisabled}
        displayName={
          stagedProcedureForValidation
            ? getProcedureDisplayText(stagedProcedureForValidation)
            : (props.placeholderText ?? '')
        }
        shouldEllipsize={true}
        dataTestAttrs={{component: 'PresentationalProcedureDropdown', element: 'root'}}
        menu={
          <SearchableMenu
            className="menu--short"
            scrollable={true}
            width={Menu.WIDTHS.FULL}
            onSelect={(procedureId: string) => {
              const procedure = props.procedures!.find((p) => p.id === procedureId)!;
              setStagedProcedureIdForValidation(procedure.id);
            }}
            optionDatas={optionDatas || []}
            selectedValue={selectedProcedureId}
          />
        }
      />
      {procedureValidation.validationStatus === ProcedureValidationStatus.IN_PROGRESS && (
        <>{/* If the validation begins to take too long, we can add a <HorizontalThrobber /> */}</>
      )}
      {procedureValidation.validationStatus === ProcedureValidationStatus.INVALID && (
        <PresentationalProcedureValidationWarning {...props} {...procedureValidation} />
      )}
    </>
  );
}

export function PresentationalProcedureNoAvailableRecipesWarningBanner(
  props: ProcedureDropdownProps
): JSX.Element {
  return (
    <div className={css(styles.banner)}>
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        supportingInfo={
          <p className={css(styles.message)}>
            <strong>
              To begin creating your study, please configure{' '}
              {getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
                stringCase: StringCase.LOWER,
                prependAorAn: true,
              })}{' '}
              by clicking{' '}
              <Link
                onClick={() => {
                  navigateToURL(TemplateCollectionDAO.collectionsURL());
                  props.onRequestCloseModal();
                }}
              >
                here
              </Link>{' '}
              or contact your Benchling administrator for assistance.
            </strong>
          </p>
        }
      />
    </div>
  );
}

export function PresentationalProcedureValidationWarning(
  props: ProcedureDropdownProps & ProcedureValidation
): JSX.Element {
  return (
    <div className={css(styles.banner)}>
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        supportingInfo={
          <p>
            <strong>
              The recipe you have selected is not valid, please fix the configuration{' '}
              <Link
                onClick={() => {
                  navigateToURL(props.editUrl);
                  props.onRequestCloseModal();
                }}
              >
                here
              </Link>{' '}
              or select a different recipe to proceed.
            </strong>
          </p>
        }
      />
    </div>
  );
}

/** @package Exported for testing */
export function shouldShowProcedureForStudyCreation(procedure: ProcedureDropdownTemplate): boolean {
  if (!BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING) {
    return true;
  }

  const currentPublishingRecord = procedure.currentPublishingRecord;
  if (currentPublishingRecord && !isInaccessible(currentPublishingRecord)) {
    return currentPublishingRecord.publishingStatus === PublishingStatus.EFFECTIVE;
  }
  return false;
}

function useProcedureDropdownQueryData(
  procedureTypeFilter: ProcedureType,
  procedureFetchPolicy: FetchPolicy = 'cache-first'
): ProcedureDropdownQueryData {
  const procedureDropdownResult = useBenchlingQuery<ProceduresDropdownListingQuery>(
    PROCEDURES_DROPDOWN_LISTING_QUERY,
    {
      renderCachedDataWhenRefetching: true,
      fetchPolicy: procedureFetchPolicy,
      variables: {
        procedureTypes: [procedureTypeFilter],
      },
    }
  );
  if (procedureDropdownResult.type !== QueryResultType.SUCCESS) {
    return {
      procedures: null,
    };
  }

  return {
    procedures: procedureDropdownResult.data.proceduresListing
      .filter((procedure) => shouldShowProcedureForStudyCreation(procedure))
      .toSorted(
        (a, b) =>
          // Sort by template name, then procedure name
          a.templateCollection.name.localeCompare(b.templateCollection.name) || a.name.localeCompare(b.name)
      ),
  };
}

export default function ProcedureDropdown(props: ProcedureDropdownProps): JSX.Element | null {
  const procedureDropdownQueryData = useProcedureDropdownQueryData(
    props.procedureType,
    props.procedureFetchPolicy
  );
  if (procedureDropdownQueryData.procedures == null) {
    return null;
  }
  return procedureDropdownQueryData.procedures.length === 0 ? (
    <PresentationalProcedureNoAvailableRecipesWarningBanner {...props} />
  ) : (
    <PresentationalProcedureDropdown {...props} {...procedureDropdownQueryData} />
  );
}

const styles = StyleSheet.create({
  banner: {
    marginTop: 10,
  },
  message: {
    marginBottom: 0,
  },
});
